import React, { useState, useEffect } from 'react';
import {
    Card, Form, Input, Button, Space, message, Typography, Row, Col, Divider
} from 'antd';
import {
    MessageOutlined, SendOutlined, CheckCircleOutlined, 
    SaveOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAutoMessages,
    updateAutoMessage
} from '../actions';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AutoMessageSettings = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    // Get state from Redux
    const autoMessagesState = useSelector(state => {
        return state.categoryManagement?.autoMessages || { 
            messages: {
                autoReplyMessage: '',
                autoConfirmMockupMessage: '',
                autoConfirmMessage: ''
            },
            loading: false,
            error: null
        };
    });

    const { messages, loading, error } = autoMessagesState;

    useEffect(() => {
        // Load auto messages on component mount
        dispatch(fetchAutoMessages());
    }, [dispatch]);

    // Update form when messages change
    useEffect(() => {
        form.setFieldsValue(messages);
    }, [messages, form]);

    const handleSave = async (messageType) => {
        try {
            const values = await form.validateFields([messageType]);
            const messageContent = values[messageType];
            
            await dispatch(updateAutoMessage(messageType, messageContent));
            message.success('Message updated successfully');
        } catch (error) {
            console.error('Error saving message:', error);
            message.error('Error occurred while saving message');
        }
    };

    const handleSaveAll = async () => {
        try {
            const values = await form.validateFields();
            
            // Save all messages
            await Promise.all([
                dispatch(updateAutoMessage('autoReplyMessage', values.autoReplyMessage)),
                dispatch(updateAutoMessage('autoConfirmMockupMessage', values.autoConfirmMockupMessage)),
                dispatch(updateAutoMessage('autoConfirmMessage', values.autoConfirmMessage))
            ]);
            
            message.success('All messages updated successfully');
        } catch (error) {
            console.error('Error saving all messages:', error);
            message.error('Error occurred while saving messages');
        }
    };

    const handleReset = () => {
        form.setFieldsValue(messages);
        message.info('Reset to original values');
    };

    const handleRefresh = () => {
        dispatch(fetchAutoMessages());
        message.info('Data reloaded');
    };

    return (
        <div className="auto-message-settings">
            <Row style={{ marginBottom: 16 }} justify="space-between" align="middle">
                <Col>
                    <Title level={4} style={{ margin: 0 }}>
                        <MessageOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        Auto Message Management
                    </Title>
                    <Text type="secondary">
                        Configure automated messages sent to customers
                    </Text>
                </Col>
                <Col>
                    <Space>
                        <Button 
                            icon={<ReloadOutlined />} 
                            onClick={handleRefresh}
                            loading={loading}
                        >
                            Reload
                        </Button>
                        <Button onClick={handleReset}>
                            Reset
                        </Button>
                        <Button 
                            type="primary" 
                            icon={<SaveOutlined />}
                            onClick={handleSaveAll}
                            loading={loading}
                        >
                            Save All
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Form
                form={form}
                layout="vertical"
                className="auto-message-form"
                initialValues={messages}
            >
                {/* Auto Reply Message */}
                <Card 
                    title={
                        <Space>
                            <SendOutlined style={{ color: '#52c41a' }} />
                            <span>Auto Reply Message</span>
                        </Space>
                    }
                    extra={
                        <Button 
                            size="small"
                            onClick={() => handleSave('autoReplyMessage')}
                            loading={loading}
                        >
                            Save
                        </Button>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <Form.Item
                        name="autoReplyMessage"
                        label="Automatic reply message"
                        help="This message will be sent automatically when customers contact for the first time"
                    >
                        <TextArea
                            rows={4}
                            placeholder="Enter automatic reply message..."
                            showCount
                            maxLength={1000}
                        />
                    </Form.Item>
                </Card>

                {/* Auto Confirm Mockup Message */}
                <Card 
                    title={
                        <Space>
                            <MessageOutlined style={{ color: '#1890ff' }} />
                            <span>Auto Confirm Mockup Message (Send mockup)</span>
                        </Space>
                    }
                    extra={
                        <Button 
                            size="small"
                            onClick={() => handleSave('autoConfirmMockupMessage')}
                            loading={loading}
                        >
                            Save
                        </Button>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <Form.Item
                        name="autoConfirmMockupMessage"
                        label="Mockup delivery message"
                        help="This message will be sent when delivering mockups to customers for review"
                    >
                        <TextArea
                            rows={4}
                            placeholder="Enter mockup delivery message..."
                            showCount
                            maxLength={1000}
                        />
                    </Form.Item>
                </Card>

                {/* Auto Confirm Message */}
                <Card 
                    title={
                        <Space>
                            <CheckCircleOutlined style={{ color: '#fa8c16' }} />
                            <span>Auto Confirm Message (Ask Customer to confirm mockup)</span>
                        </Space>
                    }
                    extra={
                        <Button 
                            size="small"
                            onClick={() => handleSave('autoConfirmMessage')}
                            loading={loading}
                        >
                            Save
                        </Button>
                    }
                >
                    <Form.Item
                        name="autoConfirmMessage"
                        label="Mockup confirmation request message"
                        help="This message will be sent to request customer confirmation of mockups"
                    >
                        <TextArea
                            rows={4}
                            placeholder="Enter mockup confirmation request message..."
                            showCount
                            maxLength={1000}
                        />
                    </Form.Item>
                </Card>
            </Form>

            <Divider />
            
            <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    💡 Tip: Use variables like {'{customerName}'}, {'{orderNumber}'}, {'{productName}'} in messages for automatic replacement
                </Text>
            </div>
        </div>
    );
};

export default AutoMessageSettings;